/**
 * Single module-flag source for BOTH runtimes (node/tsx server + Vite browser).
 * MUST match PRP-2 flag names and defaults (vite.config.ts MODULE_FLAG_DEFAULTS).
 */

export type ModuleId =
  | 'blog' | 'contact' | 'ecommerce' | 'elearning' | 'social'
  | 'payments' | 'crm' | 'email' | 'customer_auth' | 'booking'
  | 'reviews' | 'admin';

const FLAG_DEFAULTS: Record<ModuleId, boolean> = {
  blog: true,
  contact: true,
  ecommerce: false,
  elearning: false,
  social: false,
  payments: false,
  crm: false,
  email: false,
  customer_auth: false,
  booking: false,
  reviews: false,
  admin: false,
};

// Env var name: VITE_MODULE_ + upper(id)  e.g. customer_auth -> VITE_MODULE_CUSTOMER_AUTH
function envVarName(id: ModuleId): string {
  return `VITE_MODULE_${id.toUpperCase()}`;
}

// GOTCHA: guard BOTH runtimes.
// - tsx/node: import.meta exists (ESM) but import.meta.env is undefined -> optional chain.
// - Vite browser: process may be undefined -> typeof guard.
function readEnvVar(name: string): string | undefined {
  if (typeof process !== 'undefined' && process.env && process.env[name] !== undefined) {
    return process.env[name];
  }
  try {
    const meta = import.meta as unknown as { env?: Record<string, string | undefined> };
    return meta.env?.[name];
  } catch {
    return undefined;
  }
}

export function isModuleInstalled(id: ModuleId): boolean {
  const raw = readEnvVar(envVarName(id));
  if (raw === undefined || raw === '') return FLAG_DEFAULTS[id];
  return raw === 'true' || raw === '1';
}

// ---- id-shape mapping (PRP-10 D6) ----
// SERVER-visible surfaces (settings keys `modules.<id>.enabled`, the
// config/public modules map) canonically use UNDERSCORE ids ('customer_auth');
// the frontend ModuleId set uses HYPHENS ('customer-auth'). Map at boundaries
// ONLY — never migrate stored keys.
export function toServerId(id: string): string {
  return id.replace(/-/g, '_');
}
export function fromServerId(id: string): string {
  return id.replace(/_/g, '-');
}

export function getInstalledModules(): Record<ModuleId, boolean> {
  return (Object.keys(FLAG_DEFAULTS) as ModuleId[]).reduce(
    (acc, id) => {
      acc[id] = isModuleInstalled(id);
      return acc;
    },
    {} as Record<ModuleId, boolean>
  );
}
